package com.ijoin.data.api

import com.ijoin.data.api.ApiClient.addAuthToken
import com.ijoin.data.model.AcceptLegalDocumentsRequest
import com.ijoin.data.model.LegalAcceptanceStatus
import com.ijoin.data.model.LegalDocument
import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.contentType
import kotlinx.serialization.Serializable

/**
 * API client for legal document operations.
 */
class LegalApi {
    private val client = ApiClient.client
    private val baseUrl = "${ApiClient.getBaseUrl()}/api/legal"

    /**
     * Get document content by type.
     * Public endpoint - no authentication required.
     */
    suspend fun getDocument(type: String): Result<LegalDocument> {
        return safeApiCall {
            client.get("$baseUrl/documents/$type")
        }.mapCatching { response -> response.body() }
    }

    /**
     * Get current versions of all legal documents.
     * Public endpoint - no authentication required.
     */
    suspend fun getCurrentVersions(): Result<Map<String, String>> {
        return safeApiCall {
            client.get("$baseUrl/versions")
        }.mapCatching { response -> response.body() }
    }

    /**
     * Get user's acceptance status for all documents.
     * Requires authentication.
     */
    suspend fun getAcceptanceStatus(): Result<LegalAcceptanceStatus> {
        return safeApiCall {
            client.get("$baseUrl/status") {
                addAuthToken()
            }
        }.mapCatching { response -> response.body() }
    }

    /**
     * Accept legal documents.
     * Requires authentication.
     */
    suspend fun acceptDocuments(request: AcceptLegalDocumentsRequest): Result<AcceptResponse> {
        return safeApiCall {
            client.post("$baseUrl/accept") {
                addAuthToken()
                contentType(ContentType.Application.Json)
                setBody(request)
            }
        }.mapCatching { response -> response.body() }
    }

    /**
     * Withdraw AI data processing consent.
     * Requires authentication.
     */
    suspend fun withdrawAiConsent(): Result<WithdrawResponse> {
        return safeApiCall {
            client.delete("$baseUrl/ai-consent") {
                addAuthToken()
            }
        }.mapCatching { response -> response.body() }
    }

    /**
     * Check if user can publish events (has accepted organizer terms).
     * Requires authentication.
     */
    suspend fun canPublishEvent(): Result<CanPublishResponse> {
        return safeApiCall {
            client.get("$baseUrl/can-publish") {
                addAuthToken()
            }
        }.mapCatching { response -> response.body() }
    }

    @Serializable
    data class AcceptResponse(val status: String, val documentTypes: List<String>? = null)

    @Serializable
    data class WithdrawResponse(val status: String)

    @Serializable
    data class CanPublishResponse(val canPublish: Boolean)
}
